import { Injectable } from '@angular/core';
import { EmailData, QRCodeData, SmsData, VCardData, WifiData } from '@zayan-erp/sales';

@Injectable({
  providedIn: 'root'
})
export class QrCodeService {
  /**
   * Creates a QR code for a website URL
   */
  createUrlQR(url: string): QRCodeData {
    return {
      type: 'url',
      content: url
    };
  }

  /**
   * Creates a QR code for plain text
   */
  createTextQR(text: string): QRCodeData {
    return {
      type: 'text',
      content: text
    };
  }

  /**
   * Creates a QR code for email
   */
  createEmailQR(email: string, subject?: string, body?: string): QRCodeData {
    const emailData: EmailData = { email };
    if (subject) emailData.subject = subject;
    if (body) emailData.body = body;

    return {
      type: 'email',
      content: emailData
    };
  }

  /**
   * Creates a QR code for phone number
   */
  createPhoneQR(phone: string): QRCodeData {
    return {
      type: 'phone',
      content: phone
    };
  }

  /**
   * Creates a QR code for SMS
   */
  createSmsQR(phone: string, message?: string): QRCodeData {
    const smsData: SmsData = { phone };
    if (message) smsData.message = message;

    return {
      type: 'sms',
      content: smsData
    };
  }

  /**
   * Creates a QR code for WiFi network
   */
  createWifiQR(
    ssid: string,
    password: string,
    security: 'WPA' | 'WEP' | 'nopass' = 'WPA',
    hidden: boolean = false
  ): QRCodeData {
    const wifiData: WifiData = {
      ssid,
      password,
      security,
      hidden
    };

    return {
      type: 'wifi',
      content: wifiData
    };
  }

  /**
   * Creates a QR code for contact information (vCard)
   */
  createVCardQR(contact: VCardData): QRCodeData {
    return {
      type: 'vcard',
      content: contact
    };
  }

  /**
   * Creates a QR code with custom data
   */
  createCustomQR(data: string): QRCodeData {
    return {
      type: 'custom',
      content: data
    };
  }

  ensureValidUrl(url: string): string {
    if (!url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    return url;
  }

  formatEmailData(emailData: any): string {
    if (typeof emailData === 'string') {
      return `mailto:${emailData}`;
    }

    const { email, subject = '', body = '' } = emailData;
    let mailto = `mailto:${email}`;

    const params: string[] = [];
    if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
    if (body) params.push(`body=${encodeURIComponent(body)}`);

    if (params.length > 0) {
      mailto += `?${params.join('&')}`;
    }

    return mailto;
  }

  formatSmsData(smsData: any): string {
    if (typeof smsData === 'string') {
      return `sms:${smsData}`;
    }

    const { phone, message = '' } = smsData;
    return `sms:${phone}${message ? `?body=${encodeURIComponent(message)}` : ''}`;
  }

  formatWifiData(wifiData: any): string {
    if (typeof wifiData === 'string') {
      return wifiData;
    }

    const { ssid, password, security = 'WPA', hidden = false } = wifiData;
    return `WIFI:T:${security};S:${ssid};P:${password};H:${hidden ? 'true' : 'false'};;`;
  }

  formatVCard(contact: VCardData): string {
    const {
      firstName = '',
      lastName = '',
      organization = '',
      phone = '',
      email = '',
      website = '',
      address = ''
    } = contact;

    // Create a more compact vCard format
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      firstName || lastName ? `FN:${firstName} ${lastName}`.trim() : '',
      organization ? `ORG:${organization}` : '',
      phone ? `TEL:${phone}` : '',
      email ? `EMAIL:${email}` : '',
      website ? `URL:${website}` : '',
      address ? `ADR:;;${address};;;;` : '',
      'END:VCARD'
    ]
      .filter(line => line !== '')
      .join('\n');

    return vcard;
  }
}
